<script setup lang="ts">
import PublicPageLayout from "~/components/public/PublicPageLayout.vue";

const { t, locale } = useI18n();
const localeRoute = useLocaleRoute();
const { openCookiePreferences } = useCookieConsent();

const operatorName = "Anton Rubchanka";
const operatorEmail = "info@senta.lt";

// TODO: Add a real correspondence address
// Example: "Street 1-2, LT-00000 Vilnius, Lithuania"
const correspondenceAddress = "";

const updatedAt = computed(() =>
  new Intl.DateTimeFormat(locale.value, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date("2026-08-18T00:00:00Z")),
);

const sections = [
  {
    key: "general",
    blocks: [{ paragraph: "p1" }, { paragraph: "p2" }, { paragraph: "p3" }],
  },
  {
    key: "categories",
    blocks: [{ paragraph: "p1" }, { paragraph: "p2" }, { paragraph: "p3" }],
  },
  {
    key: "cookies_used",
    blocks: [{ paragraph: "p1" }, { paragraph: "p2" }],
    cookieTable: true,
  },
  {
    key: "analytics",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
  },
  {
    key: "third_party",
    blocks: [{ paragraph: "p1" }, { paragraph: "p2" }, { paragraph: "p3" }],
  },
  {
    key: "management",
    blocks: [
      { paragraph: "p1" },
      { paragraph: "p2" },
      { paragraph: "p3" },
      { paragraph: "p4" },
    ],
    settingsButton: true,
  },
  {
    key: "changes",
    blocks: [{ paragraph: "p1" }, { paragraph: "p2" }],
  },
  {
    key: "contact",
    blocks: [{ paragraph: "p1" }, { paragraph: "p2" }],
    privacyLink: true,
  },
] as const;

const cookieRows = ["token", "consent", "ga", "ga_container"] as const;

const { canonicalUrl, currentLocale } = useLocalizedSeoLinks("/cookies");

useSeoMeta({
  title: () => t("public_pages.cookies.seo.title"),
  description: () => t("public_pages.cookies.seo.description"),
  robots: "index, follow",
  ogTitle: () => t("public_pages.cookies.seo.title"),
  ogDescription: () => t("public_pages.cookies.seo.description"),
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
});
</script>

<template>
  <PublicPageLayout
    :eyebrow="$t('public_pages.common.legal')"
    :title="$t('public_pages.cookies.title')"
    :description="$t('public_pages.cookies.description')"
  >
    <div class="mb-14 w-full border-y border-app-border py-8 sm:py-9">
      <div class="grid gap-7 sm:grid-cols-2 sm:gap-10">
        <div>
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-primary">
            {{ $t("public_pages.cookies.meta.controller") }}
          </p>
          <p class="mt-2 font-bold text-app-text">
            {{ operatorName }}
          </p>
          <p class="mt-1 text-sm text-app-muted">
            {{ $t("public_pages.cookies.meta.individual") }}
          </p>
          <p v-if="correspondenceAddress" class="mt-1 text-sm text-app-muted">
            {{ $t("public_pages.cookies.meta.address") }}:
            {{ correspondenceAddress }}
          </p>
          <a
            :href="`mailto:${operatorEmail}`"
            class="mt-1 inline-block text-sm font-medium text-primary animated-underline"
          >
            {{ operatorEmail }}
          </a>
        </div>

        <div>
          <p class="text-xs font-bold uppercase tracking-[0.12em] text-primary">
            {{ $t("public_pages.cookies.meta.updated") }}
          </p>
          <p class="mt-2 font-bold text-app-text">
            {{ updatedAt }}
          </p>
          <p class="mt-1 text-sm leading-6 text-app-muted">
            {{ $t("public_pages.cookies.meta.language_note") }}
          </p>
        </div>
      </div>
    </div>

    <div class="space-y-14 sm:space-y-16">
      <section
        v-for="(section, sectionIndex) in sections"
        :key="section.key"
        class="w-full"
      >
        <h2 class="text-xl font-bold tracking-tight text-app-text sm:text-2xl">
          <span class="mr-2 text-primary"> {{ sectionIndex + 1 }}. </span>
          {{ $t(`public_pages.cookies.sections.${section.key}.title`) }}
        </h2>

        <div
          class="mt-5 space-y-4 text-[15px] leading-7 text-app-muted sm:text-base"
        >
          <p
            v-for="(block, blockIndex) in section.blocks"
            :key="`${section.key}-${blockIndex}`"
            class="pl-12 -indent-12"
          >
            <span
              class="inline-block w-12 indent-0 font-semibold tabular-nums text-primary"
            >
              {{ sectionIndex + 1 }}.{{ blockIndex + 1 }}.
            </span>
            {{
              $t(
                `public_pages.cookies.sections.${section.key}.${block.paragraph}`,
              )
            }}
          </p>

          <div
            v-if="'cookieTable' in section && section.cookieTable"
            class="pt-2"
          >
            <div
              class="hidden overflow-hidden rounded-2xl border border-app-border md:block"
            >
              <table
                class="w-full table-fixed border-collapse text-left text-sm"
              >
                <thead class="bg-app-background text-app-text">
                  <tr>
                    <th class="w-[16%] px-4 py-3 font-bold">
                      {{ $t("public_pages.cookies.table.headers.name") }}
                    </th>
                    <th class="w-[17%] px-4 py-3 font-bold">
                      {{ $t("public_pages.cookies.table.headers.provider") }}
                    </th>
                    <th class="w-[17%] px-4 py-3 font-bold">
                      {{ $t("public_pages.cookies.table.headers.category") }}
                    </th>
                    <th class="w-[34%] px-4 py-3 font-bold">
                      {{ $t("public_pages.cookies.table.headers.purpose") }}
                    </th>
                    <th class="w-[16%] px-4 py-3 font-bold">
                      {{ $t("public_pages.cookies.table.headers.duration") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in cookieRows"
                    :key="row"
                    class="border-t border-app-border align-top"
                  >
                    <td
                      class="break-words px-4 py-4 font-semibold text-app-text"
                    >
                      {{ $t(`public_pages.cookies.table.rows.${row}.name`) }}
                    </td>
                    <td class="break-words px-4 py-4">
                      {{
                        $t(`public_pages.cookies.table.rows.${row}.provider`)
                      }}
                    </td>
                    <td class="break-words px-4 py-4">
                      {{
                        $t(`public_pages.cookies.table.rows.${row}.category`)
                      }}
                    </td>
                    <td class="break-words px-4 py-4">
                      {{ $t(`public_pages.cookies.table.rows.${row}.purpose`) }}
                    </td>
                    <td class="break-words px-4 py-4">
                      {{
                        $t(`public_pages.cookies.table.rows.${row}.duration`)
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="space-y-3 md:hidden">
              <article
                v-for="row in cookieRows"
                :key="row"
                class="rounded-2xl border border-app-border p-4"
              >
                <div class="flex items-start justify-between gap-4">
                  <p class="font-bold text-app-text">
                    {{ $t(`public_pages.cookies.table.rows.${row}.name`) }}
                  </p>
                  <span
                    class="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary"
                  >
                    {{ $t(`public_pages.cookies.table.rows.${row}.category`) }}
                  </span>
                </div>

                <dl class="mt-4 space-y-3 text-sm leading-6">
                  <div>
                    <dt class="font-semibold text-app-text">
                      {{ $t("public_pages.cookies.table.headers.provider") }}
                    </dt>
                    <dd>
                      {{
                        $t(`public_pages.cookies.table.rows.${row}.provider`)
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt class="font-semibold text-app-text">
                      {{ $t("public_pages.cookies.table.headers.purpose") }}
                    </dt>
                    <dd>
                      {{ $t(`public_pages.cookies.table.rows.${row}.purpose`) }}
                    </dd>
                  </div>
                  <div>
                    <dt class="font-semibold text-app-text">
                      {{ $t("public_pages.cookies.table.headers.duration") }}
                    </dt>
                    <dd>
                      {{
                        $t(`public_pages.cookies.table.rows.${row}.duration`)
                      }}
                    </dd>
                  </div>
                </dl>
              </article>
            </div>
          </div>

          <button
            v-if="'settingsButton' in section && section.settingsButton"
            type="button"
            class="inline-flex min-h-11 items-center justify-center rounded-xl border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-white transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/25"
            @click="openCookiePreferences"
          >
            {{ $t("public_pages.cookies.settings_button") }}
          </button>

          <NuxtLink
            v-if="'privacyLink' in section && section.privacyLink"
            :to="localeRoute('/privacy-policy') || '/privacy-policy'"
            class="inline-flex font-bold text-primary animated-underline"
          >
            {{ $t("public_pages.cookies.privacy_link") }}
          </NuxtLink>
        </div>
      </section>
    </div>
  </PublicPageLayout>
</template>
